import { useIsMutating, useMutation } from "@tanstack/react-query";
import { type Dispatch, type SetStateAction } from "react";

import { type ContentType } from "../../../content/schema";
import { type UploadProgress } from "../file-input/types";
import { type FileUploadProps } from "./types";

const UPLOAD_FILES_MUTATION_KEY = ["upload-files-mutation"] as const;

export function useIsUploadFilesMutationPending() {
  const pendingMutationsCount = useIsMutating({
    mutationKey: UPLOAD_FILES_MUTATION_KEY,
  });

  return pendingMutationsCount > 0;
}

function batchUpload<TContentType extends ContentType = ContentType>({
  acceptedFiles,
  batchSize,
  uploadFile,
}: {
  acceptedFiles: File[];
  batchSize: number;
  uploadFile: (file: File) => Promise<TContentType>;
}) {
  let index = 0;
  let active = 0;
  const contents: TContentType[] = [];

  return new Promise<TContentType[]>((resolve) => {
    const runNext = () => {
      if (index >= acceptedFiles.length && active === 0) {
        resolve(contents);
        return;
      }

      if (active >= batchSize || index >= acceptedFiles.length) {
        return;
      }

      const file = acceptedFiles[index++];
      active++;

      uploadFile(file)
        .then((content) => {
          if (content) contents.push(content);
        })
        .finally(() => {
          active--;
          runNext();
        });

      runNext();
    };

    runNext();
  });
}

function createInitialUploadProgress(acceptedFiles: File[]) {
  const initialProgress = acceptedFiles.reduce((acc, file) => {
    acc[file.name] = {
      name: file.name,
      size: file.size,
      type: file.type,
      progress: 0,
    };
    return acc;
  }, {} as UploadProgress);

  return initialProgress;
}

export function useUploadFilesMutation<
  TContentType extends ContentType = ContentType,
>({
  setUploadProgress,
  batchSize = 10,

  ...params
}: Pick<FileUploadProps<TContentType>, "uploadFile" | "batchSize"> & {
  setUploadProgress: Dispatch<SetStateAction<UploadProgress | undefined>>;
}) {
  return useMutation({
    mutationKey: UPLOAD_FILES_MUTATION_KEY,
    mutationFn: async ({
      acceptedFiles,
      signal,
    }: {
      acceptedFiles: File[];
      signal: AbortSignal;
    }) => {
      const uploadFile = (file: File) =>
        params.uploadFile({
          file,
          apiFetchOptions: {
            signal,
            onUploadProgress: ({ progress }) => {
              setUploadProgress((prev) => ({
                ...prev,
                [file.name]: {
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  progress,
                },
              }));
            },
          },
        });

      setUploadProgress((prev) => ({
        ...prev,
        ...createInitialUploadProgress(acceptedFiles),
      }));

      if (batchSize === Infinity) {
        const settled = await Promise.allSettled(
          acceptedFiles.map((file) => uploadFile(file)),
        );

        return [
          ...settled
            .map((r) => (r.status === "fulfilled" ? r.value : undefined))
            .filter((f) => f !== undefined),
        ];
      }

      return batchUpload({
        acceptedFiles,
        batchSize,
        uploadFile,
      });
    },

    onSettled: () => setUploadProgress(undefined),
  });
}
